import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Box,
  Avatar,
  Chip,
} from "@mui/material";

interface Product {
  _id: string;
  name: string;
  description: string;
  price: number;
  images: string[];
  category: string;
  ishide: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface Order {
  _id: string;
  product_id: Product;
  user_id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface RecentOrdersCardProps {
  orders: Order[];
}

const RecentOrdersCard: React.FC<RecentOrdersCardProps> = ({ orders }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Get status chip color
  const getStatusChip = (status: string) => {
    const statusConfig: {
      [key: string]: {
        color:
          | "default"
          | "primary"
          | "secondary"
          | "error"
          | "warning"
          | "info"
          | "success";
        label: string;
      };
    } = {
      pending: { color: "warning", label: "Pending" },
      completed: { color: "success", label: "Completed" },
      cancelled: { color: "error", label: "Cancelled" },
      processing: { color: "info", label: "Processing" },
    };

    const config = statusConfig[status.toLowerCase()] || {
      color: "default",
      label: status,
    };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: isDark ? theme.palette.background.paper : "#fff",
        height: "100%",
        overflowX: "auto",
      }}
    >
      <CardContent>
        <Typography
          variant="h6"
          fontWeight={700}
          gutterBottom
          sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
        >
          Recent Orders
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
        >
          Latest product orders from customers
        </Typography>

        <Box sx={{ mt: 2, minWidth: { xs: "500px", sm: "auto" } }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
                  Product
                </TableCell>
                <TableCell sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
                  Price
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders && orders.length > 0 ? (
                orders.map((order) => (
                  <TableRow key={order._id}>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {/* <Avatar
                          src={
                            order.product_id.images[0]?.startsWith("http")
                              ? order.product_id.images[0]
                              : `${process.env.REACT_APP_UPLOAD_URL || ""}${
                                  order.product_id.images[0]
                                }`
                          }
                          alt={order.product_id.name}
                          sx={{ width: 40, height: 40 }}
                        ></Avatar> */}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.8rem", sm: "0.9rem" },
                          }}
                        >
                          {order.product_id.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={order.product_id.description}
                    >
                      {order.product_id.description}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                        fontWeight: 600,
                        color: "primary.main",
                      }}
                    >
                      {formatCurrency(order.product_id.price)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No orders available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RecentOrdersCard;
